"use client";

import { appLocales, getPrismicLocale, Locales } from "@/lib/i18n";
import { resolveStaticPath, resolveTranslatedPath } from "@/lib/i18n/functions";
import { segments } from "@/locales/segment";
import { AllDocumentTypes } from "@/prismicio-types";
import { classNames } from "@/helpers/class";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { RefObject, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";

interface LanguageSwitcherProps {
  document: AllDocumentTypes;
  locale: Locales;
  archive: boolean;
  template: string;
  slugs: string[];
  isStatic?: boolean;
  alternates?: Record<string, string>;
}

const LanguageSwitcher = ({
  document,
  locale,
  template,
  archive,
  slugs,
  isStatic,
  alternates,
}: LanguageSwitcherProps) => {
  const switchRef = useRef<HTMLDivElement>(null);

  const [showSelector, setVisibility] = useState<boolean>(false);

  useOnClickOutside(switchRef as RefObject<HTMLElement>, () => setVisibility(false));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setVisibility(true);
      const firstLink = window.document.querySelector<HTMLAnchorElement>("#language-switcher-menu a");
      firstLink?.focus();
    }
  };

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setVisibility(false);
      }
    };

    if (showSelector) {
      window.document.addEventListener("keydown", onEsc);
    }

    return () => window.document.removeEventListener("keydown", onEsc);
  }, [showSelector]);

  return (
    <div className="relative w-full xl:w-auto" ref={switchRef}>
      <button
        onClick={() => setVisibility(!showSelector)}
        onKeyDown={handleKeyDown}
        aria-haspopup="menu"
        aria-expanded={showSelector}
        aria-controls="language-switcher-menu"
        className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border px-2.5 py-2.5 text-sm font-semibold uppercase xl:w-auto"
      >
        {locale}
        <ChevronDown size={16} />
      </button>

      <ul
        id="language-switcher-menu"
        role="menu"
        className={classNames(
          "absolute right-0 bottom-[calc(100%+.5rem)] z-50 w-full overflow-hidden rounded-sm border border-black/10 bg-white shadow-xs transition xl:top-[calc(100%+.5rem)] xl:bottom-[unset]",
          !showSelector && "pointer-events-none translate-y-2 opacity-0"
        )}
      >
        {archive ? (
          <>
            {appLocales.locales.map((l) => {
              if (locale === l) return null;

              const segment = segments[l][template];

              const path = l === appLocales.defaultLocale ? `/${segment}` : `/${l}/${segment}`;

              return <SwitchLink path={path} label={l} key={l} />;
            })}
          </>
        ) : alternates ? (
          <>
            {Object.entries(alternates).map(([lang, path]) => {
              if (locale === lang) return null;

              return <SwitchLink path={path} label={lang} key={lang} />;
            })}
          </>
        ) : (
          <>
            {appLocales.locales.map((l) => {
              if (locale === l) return null;

              const alternate = document?.alternate_languages?.find((al) => al.lang === getPrismicLocale(l));

              const path = alternate
                ? resolveTranslatedPath(alternate)
                : isStatic
                  ? resolveStaticPath({
                      archive,
                      lang: l,
                      template,
                      actions: slugs,
                    })
                  : `/${l}/`;

              return <SwitchLink path={path} label={l} key={l} />;
            })}
          </>
        )}
      </ul>
    </div>
  );
};

const SwitchLink = ({ path, label }: { path: string; label: string }) => {
  return (
    <li role="none">
      <Link
        role="menuitem"
        href={path}
        className="inline-block w-full px-2 py-2 uppercase hover:bg-gray-200 focus:bg-gray-200 focus:outline-none"
      >
        {label}
      </Link>
    </li>
  );
};

export default LanguageSwitcher;
